import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { CityProvider } from "@/contexts/CityContext";
import { Suspense, useEffect } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { LEGACY_REDIRECTS } from "@/lib/legacyRedirects";
import { useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { gtmVirtualPageView } from "@/lib/analytics-loader";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import AnalyticsMount from "./components/AnalyticsMount";
import AnalyticsTracker from "./components/AnalyticsTracker";
import VisitorTracker from "./components/VisitorTracker";

// Eager-load homepage for fast LCP
import Index from "./pages/Index.tsx";

// Lazy-load all other routes to reduce initial JS bundle
const Oakland = lazyWithRetry(() => import("./pages/Oakland.tsx"));
const SanJose = lazyWithRetry(() => import("./pages/SanJose.tsx"));
const Commercial = lazyWithRetry(() => import("./pages/Commercial.tsx"));
const RecyclingGuide = lazyWithRetry(() => import("./pages/RecyclingGuide.tsx"));
const Sustainability = lazyWithRetry(() => import("./pages/Sustainability.tsx"));
const About = lazyWithRetry(() => import("./pages/About.tsx"));
const Careers = lazyWithRetry(() => import("./pages/Careers.tsx"));
const CareerDetail = lazyWithRetry(() => import("./pages/CareerDetail.tsx"));
const CareersPrivacy = lazyWithRetry(() => import("./pages/CareersPrivacy.tsx"));
const News = lazyWithRetry(() => import("./pages/News.tsx"));
const NewsPost = lazyWithRetry(() => import("./pages/NewsPost.tsx"));
const Contact = lazyWithRetry(() => import("./pages/Contact.tsx"));
const Billing = lazyWithRetry(() => import("./pages/Billing.tsx"));
const ResourceLibrary = lazyWithRetry(() => import("./pages/ResourceLibrary.tsx"));
const ResourceGuidePage = lazyWithRetry(() => import("./pages/ResourceGuidePage.tsx"));
const Login = lazyWithRetry(() => import("./pages/Login.tsx"));
const Signup = lazyWithRetry(() => import("./pages/Signup.tsx"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding.tsx"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword.tsx"));
const ForgotPassword = lazyWithRetry(() => import("./pages/ForgotPassword.tsx"));
const ResendVerification = lazyWithRetry(() => import("./pages/ResendVerification.tsx"));
const Auth = lazyWithRetry(() => import("./pages/Auth.tsx"));
const AuthCallback = lazyWithRetry(() => import("./pages/AuthCallback.tsx"));
const CwsAdminLogin = lazyWithRetry(() => import("./pages/CwsAdminLogin.tsx"));
const Dashboard = lazyWithRetry(() => import("./pages/customer/Dashboard.tsx"));
const CustomerRequests = lazyWithRetry(() => import("./pages/customer/Requests.tsx"));
const CustomerRequestNew = lazyWithRetry(() => import("./pages/customer/RequestNew.tsx"));
const CustomerRequestDetail = lazyWithRetry(() => import("./pages/customer/RequestDetail.tsx"));
const CustomerAccount = lazyWithRetry(() => import("./pages/customer/Account.tsx"));
const CustomerVideos = lazyWithRetry(() => import("./pages/customer/Videos.tsx"));
const CustomerResources = lazyWithRetry(() => import("./pages/customer/Resources.tsx"));
const CustomerRecyclingGuide = lazyWithRetry(() => import("./pages/customer/RecyclingGuide.tsx"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy.tsx"));
const Terms = lazyWithRetry(() => import("./pages/Terms.tsx"));
const CookieNotice = lazyWithRetry(() => import("./pages/CookieNotice.tsx"));
const Accessibility = lazyWithRetry(() => import("./pages/Accessibility.tsx"));
const Newsroom = lazyWithRetry(() => import("./pages/Newsroom.tsx"));
const Governance = lazyWithRetry(() => import("./pages/Governance.tsx"));
const Leadership = lazyWithRetry(() => import("./pages/Leadership.tsx"));
const LeaderProfile = lazyWithRetry(() => import("./pages/LeaderProfile.tsx"));
const ReportMissedPickup = lazyWithRetry(() => import("./pages/ReportMissedPickup.tsx"));
const SanJoseJunkPickup = lazyWithRetry(() => import("./pages/SanJoseJunkPickup.tsx"));
const MediaCoverage = lazyWithRetry(() => import("./pages/MediaCoverage.tsx"));
const MediaCoverageTheme = lazyWithRetry(() => import("./pages/MediaCoverageTheme.tsx"));
const DavidDuong = lazyWithRetry(() => import("./pages/DavidDuong.tsx"));
const ThirtyFiveYears = lazyWithRetry(() => import("./pages/ThirtyFiveYears.tsx"));

const NotFound = lazyWithRetry(() => import("./pages/NotFound.tsx"));
const PublicPayInvoice = lazyWithRetry(() => import("./pages/PublicPayInvoice.tsx"));
const PublicPayCommercialInvoice = lazyWithRetry(() => import("./pages/PublicPayCommercialInvoice.tsx"));
const Unsubscribe = lazyWithRetry(() => import("./pages/Unsubscribe.tsx"));
const AdminDashboard = lazyWithRetry(() => import("./pages/admin/AdminDashboard.tsx"));
const AdminNewsList = lazyWithRetry(() => import("./pages/admin/AdminNewsList.tsx"));
const AdminNewsEditor = lazyWithRetry(() => import("./pages/admin/AdminNewsEditor.tsx"));
const AdminBanners = lazyWithRetry(() => import("./pages/admin/AdminBanners.tsx"));
const AdminJobs = lazyWithRetry(() => import("./pages/admin/AdminJobs.tsx"));
const AdminApplications = lazyWithRetry(() => import("./pages/admin/AdminApplications.tsx"));
const AdminResumeBulkImport = lazyWithRetry(() => import("./pages/admin/AdminResumeBulkImport.tsx"));
const AdminUsers = lazyWithRetry(() => import("./pages/admin/AdminUsers.tsx"));
const AdminAccount = lazyWithRetry(() => import("./pages/admin/AdminAccount.tsx"));
const AdminResumeReview = lazyWithRetry(() => import("./pages/admin/AdminResumeReview.tsx"));
const AdminHrPipeline = lazyWithRetry(() => import("./pages/admin/AdminHrPipeline.tsx"));
const AdminHrEmailTemplates = lazyWithRetry(() => import("./pages/admin/AdminHrEmailTemplates.tsx"));
const AdminHrInterviews = lazyWithRetry(() => import("./pages/admin/AdminHrInterviews.tsx"));
const AdminHrOnboarding = lazyWithRetry(() => import("./pages/admin/AdminHrOnboarding.tsx"));
const AdminHrAnalytics = lazyWithRetry(() => import("./pages/admin/AdminHrAnalytics.tsx"));
const AdminHrExport = lazyWithRetry(() => import("./pages/admin/AdminHrExport.tsx"));
const AdminHrImports = lazyWithRetry(() => import("./pages/admin/AdminHrImports.tsx"));
const AdminHrImportWizard = lazyWithRetry(() => import("./pages/admin/AdminHrImportWizard.tsx"));
const AdminHrImportBatch = lazyWithRetry(() => import("./pages/admin/AdminHrImportBatch.tsx"));
const AdminHrIndeedSync = lazyWithRetry(() => import("./pages/admin/AdminHrIndeedSync.tsx"));
const AdminHrImportsRunbook = lazyWithRetry(() => import("./pages/admin/AdminHrImportsRunbook.tsx"));
const AdminLeadExport = lazyWithRetry(() => import("./pages/admin/AdminLeadExport.tsx"));
const AdminCustomerNotes = lazyWithRetry(() => import("./pages/admin/AdminCustomerNotes.tsx"));

const AdminPlaceholder = lazyWithRetry(() => import("./pages/admin/AdminPlaceholder.tsx"));
const AdminNewsletter = lazyWithRetry(() => import("./pages/admin/AdminNewsletter.tsx"));
const AdminMediaLibrary = lazyWithRetry(() => import("./pages/admin/AdminMediaLibrary.tsx"));
const AdminServiceRequests = lazyWithRetry(() => import("./pages/admin/AdminServiceRequests.tsx"));
const AdminLeads = lazyWithRetry(() => import("./pages/admin/AdminLeads.tsx"));
const AdminLeadDetail = lazyWithRetry(() => import("./pages/admin/AdminLeadDetail.tsx"));
const AdminCommercialAccounts = lazyWithRetry(() => import("./pages/admin/AdminCommercialAccounts.tsx"));
const AdminCommercialHome = lazyWithRetry(() => import("./pages/admin/AdminCommercialHome.tsx"));
const AdminCommercialEmailTemplates = lazyWithRetry(() => import("./pages/admin/AdminCommercialEmailTemplates.tsx"));
const AdminInvoiceDetail = lazyWithRetry(() => import("./pages/admin/AdminInvoiceDetail.tsx"));
const AdminCommercialAccountDetail = lazyWithRetry(() => import("./pages/admin/AdminCommercialAccountDetail.tsx"));
const AdminCommercialAccountsReports = lazyWithRetry(() => import("./pages/admin/AdminCommercialAccountsReports.tsx"));
const AdminCommercialAccountsImport = lazyWithRetry(() => import("./pages/admin/AdminCommercialAccountsImport.tsx"));
const AdminCommercialAccountsContainers = lazyWithRetry(() => import("./pages/admin/AdminCommercialAccountsContainers.tsx"));
const AdminCommercialInvoices = lazyWithRetry(() => import("./pages/admin/AdminCommercialInvoices.tsx"));
const AdminCommercialPayments = lazyWithRetry(() => import("./pages/admin/AdminCommercialPayments.tsx"));
const AdminCommercialReconciliation = lazyWithRetry(() => import("./pages/admin/AdminCommercialReconciliation.tsx"));
const AdminCommercialPaymentSubmissions = lazyWithRetry(() => import("./pages/admin/AdminCommercialPaymentSubmissions.tsx"));
const AdminInvoicePrint = lazyWithRetry(() => import("./pages/admin/AdminInvoicePrint.tsx"));
const AdminCommercialReminders = lazyWithRetry(() => import("./pages/admin/AdminCommercialReminders.tsx"));
const AdminQuickBooks = lazyWithRetry(() => import("./pages/admin/AdminQuickBooks.tsx"));
const AdminCommercialTasks = lazyWithRetry(() => import("./pages/admin/AdminCommercialTasks.tsx"));
const AdminAccountStatement = lazyWithRetry(() => import("./pages/admin/AdminAccountStatement.tsx"));
const AdminAccountStatementPrint = lazyWithRetry(() => import("./pages/admin/AdminAccountStatementPrint.tsx"));
const AdminPaymentReceiptPrint = lazyWithRetry(() => import("./pages/admin/AdminPaymentReceiptPrint.tsx"));
const AdminRateCard = lazyWithRetry(() => import("./pages/admin/AdminRateCard.tsx"));
const AdminProposals = lazyWithRetry(() => import("./pages/admin/AdminProposals.tsx"));
const AdminProposalEditor = lazyWithRetry(() => import("./pages/admin/AdminProposalEditor.tsx"));
const AdminProposalPrint = lazyWithRetry(() => import("./pages/admin/AdminProposalPrint.tsx"));
const AdminNotifications = lazyWithRetry(() => import("./pages/admin/AdminNotifications.tsx"));
const AdminEmailDeliverability = lazyWithRetry(() => import("./pages/admin/AdminEmailDeliverability.tsx"));
const AdminActivityLogs = lazyWithRetry(() => import("./pages/admin/AdminActivityLogs.tsx"));
const AdminReports = lazyWithRetry(() => import("./pages/admin/AdminReports.tsx"));
const AdminExports = lazyWithRetry(() => import("./pages/admin/AdminExports.tsx"));
const AdminSettings = lazyWithRetry(() => import("./pages/admin/AdminSettings.tsx"));
const AdminFaqs = lazyWithRetry(() => import("./pages/admin/AdminFaqs.tsx"));
const AdminMediaCoverage = lazyWithRetry(() => import("./pages/admin/AdminMediaCoverage.tsx"));
const AdminCustomerRequests = lazyWithRetry(() => import("./pages/admin/AdminCustomerRequests.tsx"));
const AdminRequestDetail = lazyWithRetry(() => import("./pages/admin/AdminRequestDetail.tsx"));
const AdminResources = lazyWithRetry(() => import("./pages/admin/AdminResources.tsx"));
const AdminVideos = lazyWithRetry(() => import("./pages/admin/AdminVideos.tsx"));
const AdminRecyclingGuide = lazyWithRetry(() => import("./pages/admin/AdminRecyclingGuide.tsx"));
const AdminServiceAlerts = lazyWithRetry(() => import("./pages/admin/AdminServiceAlerts.tsx"));
const AdminLaunchChecklist = lazyWithRetry(() => import("./pages/admin/AdminLaunchChecklist.tsx"));
const AdminGuide = lazyWithRetry(() => import("./pages/admin/AdminGuide.tsx"));
const AdminTestCenter = lazyWithRetry(() => import("./pages/admin/AdminTestCenter.tsx"));
const Faqs = lazyWithRetry(() => import("./pages/Faqs.tsx"));
const MarketingDashboard = lazyWithRetry(() => import("./pages/admin/MarketingDashboard.tsx"));
const AdminEmailCampaignEditor = lazyWithRetry(() => import("./pages/admin/AdminEmailCampaignEditor.tsx"));
const AdminMarketingAudit = lazyWithRetry(() => import("./pages/admin/AdminMarketingAudit.tsx"));
const OfferAcceptance = lazyWithRetry(() => import("./pages/OfferAcceptance.tsx"));
const PortalLogin = lazyWithRetry(() => import("./pages/portal/PortalLogin.tsx"));
const PortalHome = lazyWithRetry(() => import("./pages/portal/PortalHome.tsx"));
const PortalApplication = lazyWithRetry(() => import("./pages/portal/PortalApplication.tsx"));
const PortalClaim = lazyWithRetry(() => import("./pages/portal/PortalClaim.tsx"));
const PortalResetPassword = lazyWithRetry(() => import("./pages/portal/PortalResetPassword.tsx"));
const PortalProfile = lazyWithRetry(() => import("./pages/portal/PortalProfile.tsx"));
const PortalUnsubscribe = lazyWithRetry(() => import("./pages/portal/PortalUnsubscribe.tsx"));

const AdminCustomers = lazyWithRetry(() => import("./pages/admin/AdminCustomers.tsx"));
const AdminCustomerDetail = lazyWithRetry(() => import("./pages/admin/AdminCustomerDetail.tsx"));
const SystemMap = lazyWithRetry(() => import("./pages/admin/SystemMap.tsx"));
const AdminAiKnowledge = lazyWithRetry(() => import("./pages/admin/AdminAiKnowledge.tsx"));
const AdminAiAssistant = lazyWithRetry(() => import("./pages/admin/AdminAiAssistant.tsx"));
const AdminAiAnalytics = lazyWithRetry(() => import("./pages/admin/AdminAiAnalytics.tsx"));
const AdminAssistantVoices = lazyWithRetry(() => import("./pages/admin/AdminAssistantVoices.tsx"));
const AdminPrivacyRequests = lazyWithRetry(() => import("./pages/admin/AdminPrivacyRequests.tsx"));
const AdminAnniversaryMessages = lazyWithRetry(() => import("./pages/admin/AdminAnniversaryMessages.tsx"));

const LegacyAdminRedirect = () => {
  const loc = useLocation();
  const rest = loc.pathname.replace(/^\/admin/, "");
  return <Navigate to={`/cws-admin${rest}${loc.search}`} replace />;
};

// Legacy HR routes that pointed to /cws-admin/hr/applications — redirect to
// the real /cws-admin/applications page, preserving any query string.
const HrApplicationsRedirect = () => {
  const loc = useLocation();
  return <Navigate to={`/cws-admin/applications${loc.search}`} replace />;
};

// Old website article URLs used /post/<slug>. Send them to the newsroom URL
// for the same slug, preserving any query string.
const LegacyPostRedirect = () => {
  const loc = useLocation();
  const slug = loc.pathname.replace(/^\/post\//, "");
  return <Navigate to={`/news/post/${slug}${loc.search}`} replace />;
};

const RouteTracker = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Defer so document.title updates from Helmet first
    const t = setTimeout(() => gtmVirtualPageView(pathname + search), 80);
    return () => clearTimeout(t);
  }, [pathname, search]);
  return null;
};

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <CityProvider>
            <ErrorBoundary>
            <AnalyticsMount />
            <AnalyticsTracker />
            <VisitorTracker />
            <RouteTracker />
            <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}>
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/oakland" element={<Oakland />} />
            <Route path="/san-jose" element={<SanJose />} />
            <Route path="/commercial" element={<Commercial />} />
            <Route path="/recycling-guide" element={<RecyclingGuide />} />
            <Route path="/faqs" element={<Faqs />} />
            {/* SEO landing pages */}
            <Route path="/newsroom" element={<Newsroom />} />
            <Route path="/newsroom/media-coverage" element={<MediaCoverage />} />
            <Route path="/newsroom/media-coverage/:theme" element={<MediaCoverageTheme />} />
            <Route path="/david-duong" element={<DavidDuong />} />
            <Route path="/35-years" element={<ThirtyFiveYears />} />
            <Route path="/governance" element={<Governance />} />
            <Route path="/leadership" element={<Leadership />} />
            {/* David's profile lives at /david-duong; keep the predictable
                /leadership/<slug> pattern working for him too. */}
            <Route path="/leadership/david-duong" element={<Navigate to="/david-duong" replace />} />
            <Route path="/leadership/:slug" element={<LeaderProfile />} />
            <Route path="/report-a-missed-pickup" element={<ReportMissedPickup />} />
            <Route path="/san-jose/junk-pickup" element={<SanJoseJunkPickup />} />
            {/* INTENTIONAL LEGACY REDIRECTS, do not delete.
                Retired SEO landing pages and Wix-era URLs that are still
                bookmarked, linked and held by Google. Sources and destinations
                live in src/lib/legacyRedirects.json, which the prerender script
                also reads so the served HTML carries a canonical pointing at
                the destination page. Same idea applies to the /admin and
                /cws-admin legacy redirects further down this file. */}
            {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
              <Route key={from} path={from} element={<Navigate to={to} replace />} />
            ))}

            {/* Catch-all for the old /post/<slug> shape. Must stay AFTER the
                explicit /post overrides above so those win. */}
            <Route path="/post/:slug" element={<LegacyPostRedirect />} />
            <Route path="/sustainability" element={<Sustainability />} />
            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/privacy" element={<CareersPrivacy />} />
            <Route path="/careers/:slug" element={<CareerDetail />} />
            {/* Return path for "Save and finish later" links. Token validated server-side, noindex. */}
            <Route path="/careers/:slug/resume" element={<CareerDetail />} />

            <Route path="/news" element={<News />} />
            <Route path="/news/post/:slug" element={<NewsPost />} />
            <Route path="/news/:category" element={<News />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/resources" element={<ResourceLibrary />} />
            <Route path="/resources/:slug" element={<ResourceGuidePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/resend-verification" element={<ResendVerification />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/cookies" element={<CookieNotice />} />
            <Route path="/legal/accessibility" element={<Accessibility />} />
            
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            {/* Public "Pay My Invoice" (intentionally UNLINKED from site nav/footer). */}
            <Route path="/pay" element={<PublicPayInvoice />} />
            <Route path="/pay/:token" element={<PublicPayInvoice />} />
            {/* Public "Pay Commercial Invoice" (intentionally UNLINKED until PayPal go-live). */}
            <Route path="/pay-commercial-invoice" element={<PublicPayCommercialInvoice />} />
            <Route path="/pay-commercial-invoice/thanks" element={<PublicPayCommercialInvoice />} />
            <Route path="/offer/:token" element={<OfferAcceptance />} />
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/portal/claim" element={<PortalClaim />} />
            <Route path="/portal/reset-password" element={<PortalResetPassword />} />
            <Route path="/portal" element={<PortalHome />} />
            <Route path="/portal/profile" element={<PortalProfile />} />
            <Route path="/portal/unsubscribe" element={<PortalUnsubscribe />} />
            <Route path="/portal/application/:id" element={<PortalApplication />} />

            {/* Customer dashboard */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/requests" element={<CustomerRequests />} />
            <Route path="/dashboard/requests/new" element={<CustomerRequestNew />} />
            <Route path="/dashboard/requests/:id" element={<CustomerRequestDetail />} />
            <Route path="/dashboard/account" element={<CustomerAccount />} />
            <Route path="/dashboard/videos" element={<CustomerVideos />} />
            <Route path="/dashboard/resources" element={<CustomerResources />} />
            <Route path="/dashboard/recycling-guide" element={<CustomerRecyclingGuide />} />

            {/* Private staff portal */}
            {/* /admin habit URLs are redirected further below, where the
                subpath-preserving redirect is defined. */}

            <Route path="/cws-admin/login" element={<CwsAdminLogin />} />
            <Route path="/cws-admin" element={<AdminDashboard />} />
            <Route path="/cws-admin/news" element={<AdminNewsList />} />
            <Route path="/cws-admin/news/:id" element={<AdminNewsEditor />} />
            <Route path="/cws-admin/banners" element={<AdminBanners />} />
            <Route path="/cws-admin/jobs" element={<AdminJobs />} />
            <Route path="/cws-admin/applications" element={<AdminApplications />} />
            <Route path="/cws-admin/resume-bulk-import" element={<AdminResumeBulkImport />} />
            <Route path="/cws-admin/users" element={<AdminUsers />} />
            <Route path="/cws-admin/account" element={<AdminAccount />} />

            {/* Marketing modules */}
            <Route path="/cws-admin/newsletter" element={<AdminNewsletter />} />
            <Route path="/cws-admin/media" element={<AdminMediaLibrary />} />
            <Route path="/cws-admin/anniversary-messages" element={<AdminAnniversaryMessages />} />

            {/* HR placeholders */}
            <Route path="/cws-admin/resume-review" element={<AdminResumeReview />} />
            <Route path="/cws-admin/hr/resume-review" element={<AdminResumeReview />} />
            <Route path="/cws-admin/hr/pipeline" element={<AdminHrPipeline />} />
            <Route path="/cws-admin/hr/templates" element={<AdminHrEmailTemplates />} />
            <Route path="/cws-admin/hr/email-templates" element={<AdminHrEmailTemplates />} />
            <Route path="/cws-admin/hr/interviews" element={<AdminHrInterviews />} />
            <Route path="/cws-admin/hr/onboarding" element={<AdminHrOnboarding />} />
            <Route path="/cws-admin/hr/analytics" element={<AdminHrAnalytics />} />
            <Route path="/cws-admin/hr/applications" element={<HrApplicationsRedirect />} />
            <Route path="/cws-admin/hr/jobs" element={<Navigate to="/cws-admin/jobs" replace />} />
            <Route path="/cws-admin/hr-export" element={<AdminHrExport />} />
            <Route path="/cws-admin/hr/imports" element={<AdminHrImports />} />
            <Route path="/cws-admin/hr/imports/new" element={<AdminHrImportWizard />} />
            <Route path="/cws-admin/hr/imports/indeed" element={<Navigate to="/cws-admin/hr/imports/new?source=indeed" replace />} />
            <Route path="/cws-admin/hr/imports/runbook" element={<AdminHrImportsRunbook />} />
            <Route path="/cws-admin/hr/imports/indeed-sync" element={<AdminHrIndeedSync />} />
            <Route path="/cws-admin/hr/imports/:id" element={<AdminHrImportBatch />} />

            {/* Customer Service */}
            <Route path="/cws-admin/requests" element={<AdminCustomerRequests />} />
            <Route path="/cws-admin/requests/:id" element={<AdminRequestDetail />} />
            <Route path="/cws-admin/service-requests" element={<AdminServiceRequests />} />
            <Route path="/cws-admin/support/oakland" element={<AdminServiceRequests />} />
            <Route path="/cws-admin/support/san-jose" element={<AdminServiceRequests />} />
            <Route path="/cws-admin/support/carts" element={<AdminServiceRequests />} />
            <Route path="/cws-admin/support/missed-pickups" element={<AdminServiceRequests />} />
            <Route path="/cws-admin/support/notes" element={<AdminCustomerNotes />} />

            {/* Sales */}
            <Route path="/cws-admin/leads" element={<AdminLeads />} />
            <Route path="/cws-admin/leads/:id" element={<AdminLeadDetail />} />
            <Route path="/cws-admin/commercial-quotes" element={<Navigate to="/cws-admin/leads" replace />} />
            <Route path="/cws-admin/quotes" element={<Navigate to="/cws-admin/leads" replace />} />
            <Route path="/cws-admin/sales-notes" element={<Navigate to="/cws-admin/leads" replace />} />

            <Route path="/cws-admin/lead-export" element={<AdminLeadExport />} />
            <Route path="/cws-admin/commercial-accounts" element={<AdminCommercialAccounts />} />
            <Route path="/cws-admin/commercial-accounts/reports" element={<AdminCommercialAccountsReports />} />
            <Route path="/cws-admin/commercial-accounts/import" element={<AdminCommercialAccountsImport />} />
            <Route path="/cws-admin/commercial-accounts/containers" element={<AdminCommercialAccountsContainers />} />
            <Route path="/cws-admin/commercial-accounts/invoices" element={<AdminCommercialInvoices />} />
            <Route path="/cws-admin/commercial-accounts/payments" element={<AdminCommercialPayments />} />
            <Route path="/cws-admin/commercial-accounts/reconciliation" element={<AdminCommercialReconciliation />} />
            <Route path="/cws-admin/commercial-payments" element={<AdminCommercialPaymentSubmissions />} />
            <Route path="/cws-admin/commercial-payments/:id" element={<AdminCommercialPaymentSubmissions />} />
            <Route path="/cws-admin/commercial-accounts/invoice/:id/print" element={<AdminInvoicePrint />} />
            <Route path="/cws-admin/commercial-accounts/invoice/:id" element={<AdminInvoiceDetail />} />
            <Route path="/cws-admin/commercial" element={<AdminCommercialHome />} />
            <Route path="/cws-admin/commercial-accounts/reminders" element={<AdminCommercialReminders />} />
            <Route path="/cws-admin/integrations/quickbooks" element={<AdminQuickBooks />} />
            <Route path="/cws-admin/commercial-email-templates" element={<AdminCommercialEmailTemplates />} />
            <Route path="/cws-admin/commercial-accounts/tasks" element={<AdminCommercialTasks />} />
            <Route path="/cws-admin/commercial-accounts/receipt/:id" element={<AdminPaymentReceiptPrint />} />
            <Route path="/cws-admin/commercial-accounts/rate-card" element={<AdminRateCard />} />
            <Route path="/cws-admin/commercial-accounts/proposals" element={<AdminProposals />} />
            <Route path="/cws-admin/commercial-accounts/proposal/:id/print" element={<AdminProposalPrint />} />
            <Route path="/cws-admin/commercial-accounts/proposal/:id" element={<AdminProposalEditor />} />
            <Route path="/cws-admin/commercial-accounts/:id/statement/print" element={<AdminAccountStatementPrint />} />
            <Route path="/cws-admin/commercial-accounts/:id/statement" element={<AdminAccountStatement />} />
            <Route path="/cws-admin/commercial-accounts/:id" element={<AdminCommercialAccountDetail />} />

            {/* Super admin placeholders */}
            <Route path="/cws-admin/notifications" element={<AdminNotifications />} />
            <Route path="/cws-admin/email-deliverability" element={<AdminEmailDeliverability />} />
            <Route path="/cws-admin/activity-logs" element={<AdminActivityLogs />} />
            <Route path="/cws-admin/settings" element={<AdminSettings />} />
            <Route path="/cws-admin/settings/:tab" element={<AdminSettings />} />
            <Route path="/cws-admin/reports" element={<AdminReports />} />
            <Route path="/cws-admin/exports" element={<AdminExports />} />
            <Route path="/cws-admin/faqs" element={<AdminFaqs />} />
            <Route path="/cws-admin/media-coverage" element={<AdminMediaCoverage />} />
            <Route path="/cws-admin/resources" element={<AdminResources />} />
            <Route path="/cws-admin/videos" element={<AdminVideos />} />
            <Route path="/cws-admin/recycling-guide" element={<AdminRecyclingGuide />} />
            <Route path="/cws-admin/service-alerts" element={<AdminServiceAlerts />} />
            <Route path="/cws-admin/launch-checklist" element={<AdminLaunchChecklist />} />
            <Route path="/cws-admin/admin-guide" element={<AdminGuide />} />
            <Route path="/cws-admin/test-center" element={<AdminTestCenter />} />
            <Route path="/cws-admin/privacy-requests" element={<AdminPrivacyRequests />} />

            {/* Marketing & Analytics — unified dashboard with tabbed views */}
            <Route path="/cws-admin/marketing" element={<MarketingDashboard />} />
            {/* INTENTIONAL LEGACY REDIRECTS — do not delete. Old admin URLs
                that staff have bookmarked; they map onto the tabbed dashboard. */}
            <Route path="/cws-admin/marketing/analytics" element={<Navigate to="/cws-admin/marketing?tab=traffic" replace />} />
            <Route path="/cws-admin/marketing/ctas" element={<Navigate to="/cws-admin/marketing?tab=ctas" replace />} />
            <Route path="/cws-admin/marketing/forms" element={<Navigate to="/cws-admin/marketing?tab=forms" replace />} />
            <Route path="/cws-admin/marketing/campaigns" element={<Navigate to="/cws-admin/marketing?tab=campaigns" replace />} />
            <Route path="/cws-admin/marketing/seo" element={<Navigate to="/cws-admin/marketing?tab=seo" replace />} />
            <Route path="/cws-admin/marketing/activity" element={<Navigate to="/cws-admin/marketing?tab=realtime" replace />} />
            <Route path="/cws-admin/marketing/email/:id" element={<AdminEmailCampaignEditor />} />
            <Route path="/cws-admin/marketing/audit" element={<AdminMarketingAudit />} />


            {/* Customer Accounts */}
            <Route path="/cws-admin/customers" element={<AdminCustomers />} />
            <Route path="/cws-admin/customers/:id" element={<AdminCustomerDetail />} />

            {/* System Map */}
            <Route path="/cws-admin/system-map" element={<SystemMap />} />

            {/* AI Support Center */}
            <Route path="/cws-admin/ai-assistant" element={<AdminAiAssistant />} />
            <Route path="/cws-admin/ai-analytics" element={<AdminAiAnalytics />} />
            <Route path="/cws-admin/ai-knowledge" element={<AdminAiKnowledge />} />
            <Route path="/cws-admin/assistant-voices" element={<AdminAssistantVoices />} />

            {/* Legacy /admin → /cws-admin redirects (preserve subpath) */}
            <Route path="/admin" element={<Navigate to="/cws-admin" replace />} />
            <Route path="/admin/*" element={<LegacyAdminRedirect />} />


            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
            </Suspense>
            </ErrorBoundary>
            </CityProvider>
          </AuthProvider>
        </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
);

export default App;
